import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Separator } from './ui/separator';
import { 
  Smartphone, 
  Zap, 
  QrCode, 
  Shield, 
  Clock, 
  WifiOff, 
  Users, 
  CheckCircle2,
  ArrowRight,
  Menu,
  X,
  Phone,
  Mail
} from 'lucide-react';

const LandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Navigation */}
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 backdrop-blur-sm shadow-md' : 'bg-transparent'
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center space-x-4" data-testid="logo-section">
              <img 
                src="https://customer-assets.emergentagent.com/job_bus-ticketing-1/artifacts/pw0deilz_87133531-2.png" 
                alt="Tabularise Logo" 
                className="h-16 w-auto"
              />
              <div className="flex flex-col">
                <span className="text-2xl font-bold heading-font">TABULARISE</span>
                <span className="text-sm font-bold text-gray-600">Technologies</span>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('features')} 
                className="text-sm font-medium hover:text-gray-600 transition-colors"
                data-testid="nav-features-btn"
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection('how-it-works')} 
                className="text-sm font-medium hover:text-gray-600 transition-colors"
                data-testid="nav-how-it-works-btn"
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('technology')} 
                className="text-sm font-medium hover:text-gray-600 transition-colors"
                data-testid="nav-technology-btn"
              >
                Technology
              </button>
              <button 
                onClick={() => scrollToSection('about')} 
                className="text-sm font-medium hover:text-gray-600 transition-colors"
                data-testid="nav-about-btn"
              >
                About
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-3">
              <button 
                onClick={() => scrollToSection('features')} 
                className="block w-full text-left py-2 text-sm font-medium hover:text-gray-600"
                data-testid="mobile-nav-features-btn"
              >
                Features
              </button>
              <button 
                onClick={() => scrollToSection('how-it-works')} 
                className="block w-full text-left py-2 text-sm font-medium hover:text-gray-600"
                data-testid="mobile-nav-how-it-works-btn"
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('technology')} 
                className="block w-full text-left py-2 text-sm font-medium hover:text-gray-600"
                data-testid="mobile-nav-technology-btn"
              >
                Technology
              </button>
              <button 
                onClick={() => scrollToSection('about')} 
                className="block w-full text-left py-2 text-sm font-medium hover:text-gray-600"
                data-testid="mobile-nav-about-btn"
              >
                About
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="hero-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50"></div>
          <div className="absolute inset-0 opacity-[0.03]" 
               style={{
                 backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
               }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-8 animate-fade-in">
            {/* Main Heading */}
            <div className="space-y-4">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold heading-font text-black" data-testid="hero-title">
                IOBTIS
              </h1>
              <p className="text-xl sm:text-2xl text-gray-700 font-light" data-testid="hero-subtitle">
                Instant Offline/Online Bus Ticket Issuance System
              </p>
            </div>

            {/* Tagline */}
            <div className="py-8">
              <div className="inline-block border-2 border-black px-8 py-4" data-testid="tagline-box">
                <p className="text-2xl sm:text-3xl font-light heading-font">
                  Simple Systems, <span className="font-semibold">Real Impact</span>
                </p>
              </div>
            </div>

            {/* Description */}
            <p className="max-w-3xl mx-auto text-lg sm:text-xl text-gray-600 leading-relaxed" data-testid="hero-description">
              A revolutionary dual-app ticketing solution that transforms public transit with 
              lightning-fast validation, crypto-secured QR codes, and seamless offline functionality.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
              <Button 
                size="lg" 
                className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-lg rounded-none font-medium"
                onClick={() => scrollToSection('features')}
                data-testid="hero-learn-more-btn"
              >
                Learn More
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-black bg-white text-black hover:bg-gray-50 px-8 py-6 text-lg rounded-none font-medium"
                onClick={() => scrollToSection('how-it-works')}
                data-testid="hero-how-it-works-btn"
              >
                How It Works
              </Button>
            </div>

            {/* Platform Badges */}
            <div className="flex flex-wrap justify-center gap-6 pt-8">
              <div className="flex items-center space-x-2 text-gray-600" data-testid="platform-android">
                <Smartphone size={20} />
                <span className="text-sm font-medium">Android</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600" data-testid="platform-ios">
                <Smartphone size={20} />
                <span className="text-sm font-medium">iOS</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-600" data-testid="validation-speed">
                <Zap size={20} />
                <span className="text-sm font-medium">2-Second Validation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-4xl sm:text-5xl font-bold heading-font mb-4" data-testid="features-title">Key Features</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-testid="features-subtitle">
              Cutting-edge technology designed for real-world public transit challenges
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature Card 1 */}
            <Card className="hover-lift border-2 border-gray-200 hover:border-black transition-all duration-300 rounded-none" data-testid="feature-dual-app">
              <CardContent className="p-8">
                <div className="mb-4 p-3 bg-black w-fit">
                  <Users className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-semibold heading-font mb-3">Dual App System</h3>
                <p className="text-gray-600 leading-relaxed">
                  Separate optimized apps for passengers and conductors, each designed for their specific workflows and needs.
                </p>
              </CardContent>
            </Card>

            {/* Feature Card 2 */}
            <Card className="hover-lift border-2 border-gray-200 hover:border-black transition-all duration-300 rounded-none" data-testid="feature-qr-code">
              <CardContent className="p-8">
                <div className="mb-4 p-3 bg-black w-fit">
                  <QrCode className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-semibold heading-font mb-3">QR Code Tickets</h3>
                <p className="text-gray-600 leading-relaxed">
                  Secure, crypto-enabled QR codes that are impossible to forge and easy to validate, ensuring fare integrity.
                </p>
              </CardContent>
            </Card>

            {/* Feature Card 3 */}
            <Card className="hover-lift border-2 border-gray-200 hover:border-black transition-all duration-300 rounded-none" data-testid="feature-offline">
              <CardContent className="p-8">
                <div className="mb-4 p-3 bg-black w-fit">
                  <WifiOff className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-semibold heading-font mb-3">Offline Validation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Revolutionary offline validation technology that works seamlessly without internet connectivity.
                </p>
              </CardContent>
            </Card>

            {/* Feature Card 4 */}
            <Card className="hover-lift border-2 border-gray-200 hover:border-black transition-all duration-300 rounded-none" data-testid="feature-speed">
              <CardContent className="p-8">
                <div className="mb-4 p-3 bg-black w-fit">
                  <Clock className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-semibold heading-font mb-3">2-Second Validation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Lightning-fast ticket verification in just 2 seconds, reducing boarding time and improving passenger flow.
                </p>
              </CardContent>
            </Card>

            {/* Feature Card 5 */}
            <Card className="hover-lift border-2 border-gray-200 hover:border-black transition-all duration-300 rounded-none" data-testid="feature-security">
              <CardContent className="p-8">
                <div className="mb-4 p-3 bg-black w-fit">
                  <Shield className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-semibold heading-font mb-3">Crypto Security</h3>
                <p className="text-gray-600 leading-relaxed">
                  Military-grade cryptographic tokens embedded in tickets prevent fraud and ensure authenticity.
                </p>
              </CardContent>
            </Card>

            {/* Feature Card 6 */}
            <Card className="hover-lift border-2 border-gray-200 hover:border-black transition-all duration-300 rounded-none" data-testid="feature-realtime">
              <CardContent className="p-8">
                <div className="mb-4 p-3 bg-black w-fit">
                  <Zap className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-semibold heading-font mb-3">Instant Issuance</h3>
                <p className="text-gray-600 leading-relaxed">
                  Purchase and receive tickets instantly on your mobile device, eliminating queues and paper waste.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold heading-font mb-4" data-testid="how-it-works-title">How It Works</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-testid="how-it-works-subtitle">
              A seamless journey from ticket purchase to validation
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Passenger Flow */}
            <div className="space-y-6" data-testid="passenger-flow">
              <div className="border-2 border-black p-6">
                <h3 className="text-2xl font-semibold heading-font mb-6">For Passengers</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-black text-white flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Download & Register</h4>
                      <p className="text-sm text-gray-600">Install the passenger app on Android or iOS and create your account</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-black text-white flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Purchase Ticket</h4>
                      <p className="text-sm text-gray-600">Select your route and purchase tickets instantly using secure payment methods</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-black text-white flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Receive QR Code</h4>
                      <p className="text-sm text-gray-600">Get your crypto-secured QR code ticket immediately on your device</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-black text-white flex items-center justify-center font-bold">
                      4
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Board & Validate</h4>
                      <p className="text-sm text-gray-600">Show your QR code to the conductor for instant validation</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Conductor Flow */}
            <div className="space-y-6" data-testid="conductor-flow">
              <div className="border-2 border-black p-6">
                <h3 className="text-2xl font-semibold heading-font mb-6">For Conductors</h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-black text-white flex items-center justify-center font-bold">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Install Validator App</h4>
                      <p className="text-sm text-gray-600">Get the conductor app with specialized validation tools</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-black text-white flex items-center justify-center font-bold">
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Scan QR Code</h4>
                      <p className="text-sm text-gray-600">Use the built-in scanner to read passenger tickets</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-black text-white flex items-center justify-center font-bold">
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Instant Verification</h4>
                      <p className="text-sm text-gray-600">Validate authenticity in 2 seconds, even without internet</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-black text-white flex items-center justify-center font-bold">
                      4
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Track & Report</h4>
                      <p className="text-sm text-gray-600">View daily summaries and validated ticket statistics</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section id="technology" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="technology-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold heading-font mb-4" data-testid="technology-title">Revolutionary Technology</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-testid="technology-subtitle">
              Built on cutting-edge innovation with patent-pending offline validation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Patent Pending */}
            <div className="border-2 border-black p-8 bg-white" data-testid="tech-patent">
              <div className="flex items-start space-x-4">
                <CheckCircle2 className="flex-shrink-0 mt-1" size={32} />
                <div>
                  <h3 className="text-2xl font-semibold heading-font mb-3">Patent Pending</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Our innovative offline validation method is currently under patent protection, 
                    representing a breakthrough in transit ticketing technology.
                  </p>
                  <div className="inline-block bg-black text-white px-4 py-2 text-sm font-medium">
                    PATENT APPLICATION FILED
                  </div>
                </div>
              </div>
            </div>

            {/* Crypto Security */}
            <div className="border-2 border-black p-8 bg-white" data-testid="tech-crypto">
              <div className="flex items-start space-x-4">
                <Shield className="flex-shrink-0 mt-1" size={32} />
                <div>
                  <h3 className="text-2xl font-semibold heading-font mb-3">Cryptographic Tokens</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Each ticket contains a unique cryptographic token that ensures authenticity 
                    and prevents duplication, fraud, or tampering. The system validates tickets 
                    both online and offline using advanced cryptographic algorithms.
                  </p>
                </div>
              </div>
            </div>

            {/* Real-time Integration */}
            <div className="border-2 border-black p-8 bg-white" data-testid="tech-integration">
              <div className="flex items-start space-x-4">
                <Zap className="flex-shrink-0 mt-1" size={32} />
                <div>
                  <h3 className="text-2xl font-semibold heading-font mb-3">Live Transit Data</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Integrated with Google Maps transit and live BMTC bus routes for real-time 
                    route planning, arrival predictions, and location-based services.
                  </p>
                </div>
              </div>
            </div>

            {/* Performance */}
            <div className="border-2 border-black p-8 bg-white" data-testid="tech-performance">
              <div className="flex items-start space-x-4">
                <Clock className="flex-shrink-0 mt-1" size={32} />
                <div>
                  <h3 className="text-2xl font-semibold heading-font mb-3">Ultra-Fast Performance</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Optimized for speed with 2-second validation time, ensuring smooth passenger 
                    flow during peak hours. The system handles high transaction volumes without delays.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="about-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold heading-font mb-4" data-testid="about-title">About Tabularise</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-testid="about-subtitle">
              Simple Systems, Real Impact
            </p>
          </div>

          <div className="border-2 border-black p-8 md:p-12 space-y-6">
            <p className="text-lg text-gray-700 leading-relaxed">
              At <span className="font-semibold">Tabularise Technologies</span>, we believe that the most effective 
              solutions are built on simplicity. Our mission is to create technology that solves real-world problems 
              with elegant, straightforward systems that anyone can use.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              IOBTIS represents our commitment to this philosophy—a powerful ticketing platform that transforms 
              public transportation while remaining accessible to all users, from daily commuters to transit operators.
            </p>

            <Separator className="my-8" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-4">
              <div className="text-center" data-testid="about-stat-validation">
                <div className="text-4xl font-bold heading-font mb-2">2s</div>
                <div className="text-sm text-gray-600">Validation Time</div>
              </div>
              <div className="text-center" data-testid="about-stat-security">
                <div className="text-4xl font-bold heading-font mb-2">100%</div>
                <div className="text-sm text-gray-600">Fraud Prevention</div>
              </div>
              <div className="text-center" data-testid="about-stat-platforms">
                <div className="text-4xl font-bold heading-font mb-2">2</div>
                <div className="text-sm text-gray-600">Platforms (iOS & Android)</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl sm:text-4xl font-bold heading-font" data-testid="cta-title">
            Ready to Transform Public Transit?
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto" data-testid="cta-description">
            IOBTIS is coming soon to Android and iOS. Join us in revolutionizing the way people travel.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button 
              size="lg" 
              className="bg-white text-black hover:bg-gray-200 px-8 py-6 text-lg rounded-none font-medium"
              disabled
              data-testid="cta-android-btn"
            >
              <Smartphone className="mr-2" size={20} />
              Coming to Android
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-white bg-transparent text-white hover:bg-white hover:text-black px-8 py-6 text-lg rounded-none font-medium"
              disabled
              data-testid="cta-ios-btn"
            >
              <Smartphone className="mr-2" size={20} />
              Coming to iOS
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t-2 border-gray-200 py-12 px-4 sm:px-6 lg:px-8" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div className="space-y-4" data-testid="footer-company">
              <div className="flex items-center space-x-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_bus-ticketing-1/artifacts/pw0deilz_87133531-2.png" 
                  alt="Tabularise Logo" 
                  className="h-14 w-auto"
                />
                <div className="flex flex-col">
                  <span className="text-xl font-bold heading-font">TABULARISE</span>
                  <span className="text-sm font-bold text-gray-600">Technologies</span>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Simple Systems, Real Impact
              </p>
            </div>

            {/* Product */}
            <div data-testid="footer-product">
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-black">Features</button></li>
                <li><button onClick={() => scrollToSection('how-it-works')} className="hover:text-black">How It Works</button></li>
                <li><button onClick={() => scrollToSection('technology')} className="hover:text-black">Technology</button></li>
              </ul>
            </div>

            {/* Company */}
            <div data-testid="footer-about">
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><button onClick={() => scrollToSection('about')} className="hover:text-black">About Us</button></li>
                <li className="text-gray-400">Careers (Coming Soon)</li>
              </ul>
            </div>

            {/* Contact */}
            <div data-testid="footer-contact">
              <h4 className="font-semibold mb-4">Contact Us</h4>
              <div className="space-y-4 text-sm">
                {/* COO */}
                <div className="space-y-1">
                  <p className="font-medium text-black">Mr Ashutosh Dwivedi</p>
                  <p className="text-xs text-gray-600">Chief Operating Officer</p>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Phone size={14} />
                    <a href="tel:+919620356777" className="hover:text-black">+91-9620356777</a>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Mail size={14} />
                    <a href="mailto:ashutosh.dwivedi@tabularise.com" className="hover:text-black break-all">ashutosh.dwivedi@tabularise.com</a>
                  </div>
                </div>
                
                {/* CTO */}
                <div className="space-y-1 pt-3">
                  <p className="font-medium text-black">Mr Deepak Krishnamurthy</p>
                  <p className="text-xs text-gray-600">Chief Technology Officer</p>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Phone size={14} />
                    <a href="tel:+919538028100" className="hover:text-black">+91-9538028100</a>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Mail size={14} />
                    <a href="mailto:deepak.krishnamurthy@tabularise.com" className="hover:text-black break-all">deepak.krishnamurthy@tabularise.com</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Separator className="my-8" />

          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-600" data-testid="footer-copyright">
              © 2025 Tabularise Technologies. All rights reserved.
            </p>
            <p className="text-sm text-gray-600" data-testid="footer-domain">
              www.tabularise.com
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
